import { doc, onSnapshot, getFirestore } from "firebase/firestore";
import { useState, useEffect } from "react";
import { ZodType } from "zod";

export interface UseRealtimeDocumentData<T> {
  data: T | undefined;
  loading: boolean;
  error: Error | undefined;
}

/**
 * Custom hook to fetch real-time document data from Firestore with schema validation.
 * @param {string} path - The Firestore document path.
 * @param {ZodType<T>} schema - The Zod schema for validating the document.
 * @param {string | undefined} id - The document ID to fetch.
 * @returns {UseRealtimeDocumentData<T>} - An object containing data, loading state, and error.
 */
export const useRealtimeDocumentData = <T>(path: string, schema: ZodType<T, any, any>, id: string | undefined): UseRealtimeDocumentData<T> => {
  const [data, setData] = useState<T | undefined>(undefined);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | undefined>(undefined);

  useEffect(() => {
    if (!id) {
      // If no document ID is provided, reset state and exit early
      setData(undefined);
      setLoading(false);
      setError(undefined);
      return;
    }

    setLoading(true);

    // Set up real-time listener for the Firestore document
    const unsubscribe = onSnapshot(
      doc(getFirestore(), path, id),
      (snapshot) => {
        const parsedData = schema.safeParse({
          id: snapshot.id,
          ...snapshot.data(),
        });

        if (parsedData.success) {
          setData(parsedData.data);
          setError(undefined);
        } else {
          setData(undefined);
          setError(new Error("Failed to parse data"));
        }

        setLoading(false);
      },
      (error) => {
        setData(undefined);
        setError(error);
        setLoading(false);
      }
    );

    // Clean up the listener when the component is unmounted
    return () => unsubscribe();
  }, [id, path, schema]);

  return {
    data,
    loading,
    error,
  };
};
